import { countDuplicates } from '@core/logic';
import { Fade } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { useTheme } from '@mui/material/styles';
import clsx from 'clsx';
import { RefObject, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ConnectionEditDialog } from './ConnectionEditDialog';
import type { Connection } from './types';

export const ConnectionView = ({
  connection: { id, inPipe, outPipe },
  containerRef,
  canvasRef,
  label,
  isHighlighted,
  onClickConnection,
  resizeTrigger,
}: {
  connection: Connection;
  containerRef: RefObject<HTMLDivElement | null>;
  canvasRef: RefObject<SVGSVGElement | null>;
  isHighlighted: boolean;
  label?: string;
  onClickConnection: (id: string) => void;
  resizeTrigger: number;
}) => {
  // The y-coordinates are what changes, while the x-coordinates are static (the connections always have the same width in the layout).
  const startX = 0;
  const [endX, setEndX] = useState(0);
  const [startY, setStartY] = useState(0);
  const [endY, setEndY] = useState(0);

  useLayoutEffect(() => {
    const containerRect = containerRef.current?.getBoundingClientRect();
    const offsetTop = containerRect?.top ?? 0;

    const inRect = inPipe.ref.current?.getBoundingClientRect();
    if (inRect) {
      const nextStartY = inRect.top + inRect.height / 2 - offsetTop;
      setStartY(nextStartY);
    }

    const outRect = outPipe.ref.current?.getBoundingClientRect();
    if (outRect) {
      const nextEndY = outRect.top + outRect.height / 2 - offsetTop;
      setEndY(nextEndY);
    }

    const nextEndX = canvasRef.current?.clientWidth ?? 0;
    setEndX(nextEndX);
  }, [
    containerRef,
    canvasRef,
    inPipe.ref,
    outPipe.ref,
    inPipe.ref.current,
    outPipe.ref.current,
    resizeTrigger,
  ]);

  return (
    <>
      <path
        className="hoverZone"
        onClick={() => onClickConnection(id)}
        d={`
          M ${[startX, startY]} 
          C ${[endX / 2, startY]} 
            ${[endX / 2, endY]}
            ${[endX, endY]}
        `}
        data-testid={`connection-${inPipe.id}:${outPipe.id}`}
      />
      <Fade in>
        <path
          className={clsx('connection', isHighlighted && 'highlighted')}
          d={`
          M ${[startX, startY]} 
          C ${[endX / 2, startY]} 
            ${[endX / 2, endY]}
            ${[endX, endY]}
        `}
        />
      </Fade>

      {label && (
        <text x={endX / 2} y={startY + (endY - startY) / 2}>
          {label}
        </text>
      )}
    </>
  );
};

export const ConnectionsView = ({
  highlightedConnection,
  containerRef,
  connections,
}: {
  containerRef: RefObject<HTMLDivElement | null>;
  highlightedConnection?: number;
  connections: Connection[];
}) => {
  const theme = useTheme();
  const canvasRef = useRef<SVGSVGElement>(null);

  const [selectedConnectionId, setSelectedConnectionId] = useState<string | null>(null);
  const selectedConnection = connections.find((c) => c.id === selectedConnectionId);

  const [resizeTrigger, setResizeTrigger] = useState(0);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const observer = new ResizeObserver(() => {
      setResizeTrigger((prev) => prev + 1);
    });

    observer.observe(container);

    // Observe child cards and accordions to catch size updates during expansion/collapse transitions
    const cardsAndAccordions = container.querySelectorAll('.MuiCard-root, .MuiAccordion-root');
    cardsAndAccordions.forEach((el) => {
      observer.observe(el);
    });

    // Recalculate on any scroll within the container subtree
    const handleScroll = () => {
      setResizeTrigger((prev) => prev + 1);
    };

    container.addEventListener('scroll', handleScroll, { capture: true });
    window.addEventListener('resize', handleScroll);

    return () => {
      observer.disconnect();
      container.removeEventListener('scroll', handleScroll, { capture: true });
      window.removeEventListener('resize', handleScroll);
    };
  }, [containerRef, connections]);

  const countConnectionDuplicates = countDuplicates(
    connections.map(({ inPipe, outPipe }) => [inPipe, outPipe]),
  );

  return (
    <>
      <Grid
        container
        size={{ xs: 1.6 }}
        rowSpacing={2}
        sx={{
          '.connections': {
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            zIndex: 0,
            boxSizing: 'border-box',
          },
          '& .connection': {
            fill: 'none',
            strokeWidth: 1,
            pointerEvents: 'none',
            transition: theme.transitions.create(['stroke', 'stroke-width']),
            stroke: theme.palette.secondary.main,
            '&.isHighlighted': {
              strokeWidth: 3,
            },
          },
          '& .hoverZone': {
            fill: 'none',
            strokeWidth: 10,
            stroke: 'transparent',
            cursor: 'pointer',
          },
          '& .hoverZone:hover + .connection': {
            cursor: 'pointer',
            strokeWidth: 3,
            stroke: theme.palette.error.main,
          },
          '& .highlighted': {
            strokeWidth: 2,
            stroke: theme.palette.success.main,
          },
          '& text': {
            ...theme.typography.caption,
            fill: theme.palette.text.primary,
            pointerEvents: 'none',
            userSelect: 'none',
            textShadow: '0 0 3px black',
          },
        }}
      >
        <svg className="connections" ref={canvasRef}>
          {connections.map((connection, index) => {
            const count = countConnectionDuplicates([connection.inPipe, connection.outPipe]);
            const isHighlighted = highlightedConnection === index;
            return (
              <ConnectionView
                label={count > 1 ? String(count) : undefined}
                connection={connection}
                containerRef={containerRef}
                canvasRef={canvasRef}
                key={index}
                isHighlighted={isHighlighted}
                onClickConnection={(id) => setSelectedConnectionId(id)}
                resizeTrigger={resizeTrigger}
              />
            );
          })}
        </svg>
      </Grid>
      <ConnectionEditDialog
        selectedConnection={selectedConnection}
        onClose={() => setSelectedConnectionId(null)}
        key={selectedConnection?.id}
      />
    </>
  );
};
